// The page's actions: what an applet may ask this page to do by name (@webtop/api's page.run(name, args), the `page`
// permission), the things a user does through its menus and overlays. The assistant (applets/assistant) runs the
// ones its model answers with this way. Each checks its own arguments, since what an applet passes on may have come
// from an AI model; a name not listed is refused. A new one is an entry in ACTIONS (and in docs/applets.md).
import { toggleAdd } from './add.js';
import { lockNow } from './lock.js';
import { favoriteMenuItem, goHome, homeMenuItem, isFavorite, isHomePinned } from './notch.js';
import { APPLETS, appletService, LAUNCHER, SETTINGS, webtops } from './applets.js';
import { closePanel, shownTab } from './notch-panel.js';
import { closeOverlay, openView } from './overlay.js';
import { $webtop, byId, slug, webtop } from './state.js';
import { parentGroup } from './tiles.js';
import { confirmDialog, toast } from './ui.js';

// Each is `(args)`; it may be async.
export const ACTIONS = {
  // Nothing is gone yet: the usual confirmation, then the DELETE route and the root webtop.
  deleteWebtop: () => webtops((w) => w.deleteWebtop()),
  // Point tiles out: the first scrolled into view, each flashed (style.css, .tile.flash). A group member is not drawn
  // on the webtop, so the group holding it (or the one holding that) flashes instead.
  highlight: ({ ids }) => {
    if (!Array.isArray(ids)) return;
    const els = [...new Set(ids.filter((id) => typeof id === 'string').map(drawnTile).filter(Boolean))];
    els[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    for (const el of els) {
      el.classList.remove('flash');
      void el.offsetWidth; // restart the animation on a tile still flashing from the last time
      el.classList.add('flash');
      el.addEventListener('animationend', () => el.classList.remove('flash'), { once: true });
    }
  },
  // The browser's own lists, through the same menu items the webtop menu offers (they toast what they did); a state
  // already as asked is left alone, and the reply says so.
  favorite: ({ on }) => { if (typeof on === 'boolean' && isFavorite(slug) !== on) favoriteMenuItem().action(); },
  home: ({ do: what }) => {
    if (what === 'go') return goHome();
    if ((what === 'set' && !isHomePinned()) || (what === 'unset' && isHomePinned())) homeMenuItem()[0].action();
  },
  lock: () => { if (webtop?.unlocked && (webtop.locked || webtop.owned)) return lockNow(); },
  export: ({ as }) => (as === 'json' || as === 'bookmarks' ? webtops((w) => (as === 'json' ? w.exportJson() : w.exportBookmarks())) : undefined),
  show: ({ view }) => VIEWS[view]?.(),
  find: ({ text }) => (typeof text === 'string' && text.trim() ? webtops((w) => w.search(text.trim())) : undefined),
  scroll: ({ to }) => {
    if (to === 'top') scrollTo({ top: 0, behavior: 'smooth' });
    else if (to === 'bottom') scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  },
  visit: ({ url }) => {
    const u = webAddress(url);
    if (u) return openTab(u.href, u.host);
  },
  tab: () => openTab(`${location.origin}/`, 'a new tab'),
  close: () => {
    if (openView()) return closeOverlay();
    if (shownTab()) return closePanel();
    return toast('Nothing is open to close.', { kind: 'info' });
  },
  back: () => history.back(),
};

// An http(s) address, checked again here: it may have come from an AI model.
function webAddress(text) {
  try {
    const u = new URL(text);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u : null;
  } catch { return null; }
}

// A new browser tab at `url`. A browser opens one only shortly after a click or key press, and an applet may ask long
// after one (the assistant's answer comes seconds after the talk button was let go), so a blocked tab is offered as a
// button instead: that click opens it. The tab never gets a handle on this page (opener cleared).
async function openTab(url, label) {
  const tab = window.open(url, '_blank');
  if (tab) { tab.opener = null; return; }
  const ok = await confirmDialog({
    title: `Open ${label}?`,
    text: 'The browser held back a new tab opened for you.',
    button: 'Open',
  });
  if (ok) window.open(url, '_blank', 'noopener');
}

const VIEWS = {
  settings: () => appletService(SETTINGS).then((s) => s.open()),
  webtops: () => webtops((w) => w.open()),
  add: () => toggleAdd(true),
  launcher: () => appletService(LAUNCHER).then((l) => l.openMain()),
  applets: () => appletService(APPLETS).then((a) => a.open()),
};

// The element showing tile `id` on the webtop: its own, or its nearest group's when it is inside one.
function drawnTile(id) {
  for (let s = byId(id); s; s = parentGroup(s)) {
    const el = $webtop.querySelector(`.tile[data-id="${CSS.escape(s.id)}"]`);
    if (el) return el;
  }
  return null;
}

/** Runs the page action `name` with `args` (an object); answers what it answers. */
export function runPageAction(name, args = {}) {
  if (typeof name !== 'string' || !Object.hasOwn(ACTIONS, name)) throw new Error(`The page has no action "${name}"`);
  return ACTIONS[name](args && typeof args === 'object' ? args : {});
}
